import type { ReactElement } from 'react';
import { Fragment, forwardRef, useEffect, useLayoutEffect, useRef, useState } from 'react';

import type { IconList, InputSize } from '@snowit/icetypes';

import useA11y from '@snowit/hooks/useA11y';
import useBreakpoints from '@snowit/hooks/useBreakpoints';
import Portal from '@snowit/hooks/usePortals';
import { Keyboard } from '@snowit/iceconst';
import { makeCSS } from '@snowit/utils/misc';
import testId from '@snowit/utils/testId';

import DropdownHeader from '../../utils/DropdownItems/DropdownHeader';
import { m } from '../../utils/Motion';
import Input from '../Input';

import cx from 'classnames';
import s from './InputDropdown.module.scss';

export interface IInputDropdown {
  size?: InputSize;
  label?: string;
  mounted?: boolean;
  placeholder?: string;
  hint?: string;
  icon?: IconList;
  emoji?: boolean;
  valueLabel?: string;
  valueSecondary?: string;
  dropdown: ReactElement;
  footer?: ReactElement;
  name?: string;
  onChange?: (input?: string) => void;
  loading?: boolean;
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  error?: boolean;
  errorMsg?: string;
  id?: string;
  fakeInput?: boolean;
  classNames?: {
    wrapper?: string;
    wrapInput?: string;
    input?: string;
    label?: string;
    dropdown?: string;
    bodyDropdown?: string;
  };
  /** Jest/Cypress test string */
  test?: string;
  onMount?: (b: boolean) => void;
}

const InputDropdown = forwardRef((props: IInputDropdown, ref: any) => {
  const {
    size,
    label,
    placeholder,
    hint,
    icon,
    valueLabel,
    valueSecondary,
    dropdown,
    footer,
    name,
    onChange,
    loading,
    required,
    readOnly,
    disabled,
    error,
    errorMsg,
    // id,
    classNames,
    test,
    fakeInput = true,
  } = props;
  const a11yH = useA11y();

  const dropdownId = a11yH.makeId(props.id, 'dropdown');

  const parentRef = useRef<any>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const childRef = useRef<any>(null);
  const { minWidth } = useBreakpoints();

  const [wParent, setWParent] = useState(0);
  const [vlabel, setVlabel] = useState(valueLabel);
  const [showDropdown, setShowDropdown] = useState(Boolean(props.mounted));
  const wasDropdownOpen = useRef(false);

  useEffect(() => {
    setVlabel(valueLabel);
  }, [valueLabel]);

  useEffect(() => {
    if (typeof props.mounted === 'boolean') {
      setShowDropdown(props.mounted);
    }
  }, [props.mounted]);

  const handleDropdown = () => {
    setShowDropdown(!showDropdown);
    if (typeof props.onMount === 'function') {
      props.onMount(!showDropdown);
    }
  };

  const handleClose = () => {
    setShowDropdown(false);
    if (typeof props.onMount === 'function') {
      props.onMount(false);
    }
  };

  useEffect(() => {
    if (ref?.current) {
      ref.current = parentRef.current;
    }
  }, [ref?.current, parentRef.current]);

  useEffect(() => {
    if (showDropdown) {
      (contentRef.current?.firstElementChild as HTMLElement)?.focus();
    } else if (wasDropdownOpen.current) {
      (parentRef.current as HTMLElement)?.focus();
    }
    // Restore focus only after closing, never when a closed input first mounts.
    wasDropdownOpen.current = showDropdown;
  }, [showDropdown]);

  useEffect(() => {
    if (childRef.current instanceof Element) {
      childRef.current.scrollIntoView();
    }
  }, [childRef.current, showDropdown]);

  useLayoutEffect(() => {
    if (parentRef.current instanceof HTMLElement) {
      setWParent(parentRef.current.offsetWidth);
    }
  }, []);

  const renderHeaderDropdown = () => {
    if (minWidth.sm) return null;
    return (
      <DropdownHeader
        classNames={{ wrapper: s.headerDropdown }}
        title={placeholder ? placeholder : String(label)}
        onClick={handleClose}
      />
    );
  };

  const renderFooter = () => {
    if (!footer) return null;
    return <div className={s.footer}>{footer}</div>;
  };

  // const renderSecondary = () => {
  //   if (typeof valueSecondary !== 'function') return undefined;
  //   const found = options.find((o) => o.value === value || o.key === value);
  //   if (!found) return undefined;
  //   return valueSecondary(found);
  // };

  let motionVariants = {};

  if (minWidth.sm) {
    motionVariants = {
      open: { opacity: 1, height: 'auto' },
      collapsed: { opacity: 0, height: 0 },
    };
  } else {
    motionVariants = {
      open: { opacity: 1, translateY: '0%' },
      collapsed: { opacity: 1, translateY: '100%' },
    };
  }

  const Dropdown = (
    <m.div
      ref={contentRef}
      id={dropdownId}
      className={cx(s.dropdown, classNames?.dropdown, { [s.hasFooter]: footer })}
      style={makeCSS({ '--w-parent': wParent })}
      initial='collapsed'
      animate='open'
      exit='collapsed'
      variants={motionVariants}
      transition={{
        type: 'tween',
        duration: 0.25,
        ease: [0.215, 0.61, 0.355, 1],
      }}
      role={'combobox'}
      aria-orientation={'vertical'}
      tabIndex={-1}
      {...testId(test, 'dropdown')}
    >
      {renderHeaderDropdown()}
      <div className={cx(s.bodyDropdown, classNames?.bodyDropdown)} role={'listbox'} tabIndex={-1}>
        {dropdown}
      </div>
      {renderFooter()}
    </m.div>
  );

  return (
    <Fragment>
      <Input
        classNames={{
          wrapper: classNames?.wrapper,
          wrapInput: classNames?.wrapInput,
          input: classNames?.input,
          label: classNames?.label,
        }}
        ref={parentRef}
        type={'text'}
        size={size}
        icon={icon}
        label={label}
        hint={hint}
        placeholder={placeholder}
        name={name}
        value={vlabel}
        valueSecondary={valueSecondary}
        error={error}
        errorMsg={errorMsg}
        dropdown
        fakeInput={fakeInput}
        required={required}
        loading={loading}
        readOnly={readOnly}
        disabled={disabled}
        onClick={handleDropdown}
        onChange={(args) => {
          if (fakeInput) return;
          setVlabel(args.value);
          if (typeof onChange === 'function') {
            onChange(args.value);
          }
        }}
        onKeyDown={(event) => {
          if (event.key === Keyboard.ENTER) {
            handleDropdown();
          }
        }}
        a11y={{ role: 'combobox', expanded: showDropdown, controls: dropdownId }}
        test={test}
      />
      <Portal
        mounted={showDropdown}
        content={Dropdown}
        bindTo={parentRef.current}
        onClose={handleClose}
        fullscreen={!minWidth.sm}
        clickToHide
        position={'adaptive'}
        escToHide={true}
      />
    </Fragment>
  );
});

export default InputDropdown;
